/**
 * ArticleList — ES6 module for the article-list / magazine grid component.
 *
 * - Pure ES6 class (no T.Module, no Knockout)
 * - Listens to native CustomEvents (viewportChange, tabItemShown)
 * - Uses ArticleListService (fetch-based) for search
 * - Uses native DOM APIs, URLSearchParams, scrollIntoView
 * - Masonry loaded as a window global by app.js; images are awaited via
 *   waitForImages from @utils/imageLoadUtils.
 *
 * TODO(cleanup): The original header referenced `imagesLoaded` as a window
 * global set by app.js. That is stale — app.js no longer exposes it and this
 * module uses waitForImages instead. Remove the outdated reference (and the
 * matching `window.imagesLoaded` mock in ArticleList.test.ts) when touching
 * this file.
 *
 * @ticket AB-8816
 */

import { waitForImages } from "@utils/imageLoadUtils";
import { ArticleListService, SearchResponse } from "./ArticleListService";
import { DateStringCreator as dateStringCreator } from "@utils/DateStringCreator";
import { handleCustomUDL } from "@utils/UDLUtils";
import {
  MOUSE_EVENT_BUTTON,
  isMainOrMiddleClick,
} from "@utils/MouseEventButton";
import getHashParams from "@utils/getHashParams";
import { focusUnfocusableElement } from "@utils/focusUtils";

// --- Constants -----------------------------------------------------------------

const MASONRY_LOADED_CLASS = "masonry-loaded";
const MAGAZINE_ARTICLE_PAGE_CLASS = "magazinearticlepage";
const SHOW_MORE_BUTTON_SELECTOR = ".comp-article-list__button--load-more";
const TAG_FILTER_RADIO_SELECTOR = ".js-tag-filter-input-radio";
const ARTICLE_LINK_SELECTOR = ".comp-article a";
const CARD_TITLE_SELECTOR = ".card-title";
const TAB_CLASS_NAME = ".comp-tab";
const SPINNER_SELECTOR = ".js-spinner";
const ARTICLE_TEMPLATE_SELECTOR = ".js-article-template";

// --- Interfaces ----------------------------------------------------------------

interface Tag {
  id: string;
  text: string;
  cursorMark: string;
  capitalized: boolean;
}

// --- Class ---------------------------------------------------------------------

export class ArticleList {
  private el: HTMLElement;
  private formEl: HTMLFormElement | null;
  private service: ArticleListService;
  private msnry: any = null;

  // State
  private cursorMark = "";
  private tagCursorMark = "";
  private tabCursorMark = "";
  private latestHash = "";
  private count = "";
  private initialCount = "";
  private activeTab = "";
  private currentTabId = "";
  private tabInitialized = false;
  private tags: Tag[] = [];
  private teaser: any = null;
  private label = "";
  private labelToday = "";
  private labelDaysAgo = "";
  private labelExclusiveContent = "";

  // Search results
  private searchResults: any[] = [];
  private resultsLength = 0;

  // Article card <template> element (provided by AEM backend)
  private templateEl: HTMLTemplateElement | null = null;

  // Cached status element references
  private spinnerEl: HTMLElement | null = null;
  private noResultsEl: HTMLElement | null = null;
  private errorMessageEl: HTMLElement | null = null;

  // Bound event handlers (for cleanup)
  private boundOnViewportChange: (e: Event) => void;
  private boundOnTabItemShown: (e: Event) => void;
  private boundOnHashChange: () => void;

  constructor(el: HTMLElement) {
    this.el = el;
    this.formEl = el.querySelector(".js-form") as HTMLFormElement | null;

    // Read config from data attributes (native DOM)
    const apiUrl = el.getAttribute("data-api-url") || "";
    this.service = new ArticleListService(apiUrl);

    const rawTags = JSON.parse(el.getAttribute("data-tags") || "[]");
    this.tags = Array.isArray(rawTags) ? rawTags : [];
    this.currentTabId = this.getIdOfCurrentTab();
    this.tags = this.tags.map((t) => ({
      ...t,
      id: this.currentTabId + "-" + t.id,
    }));

    const rawTeaser = JSON.parse(el.getAttribute("data-teaser") || "null");
    this.teaser = rawTeaser;

    this.tabCursorMark = el.getAttribute("data-url-initial-params") || "";
    this.label = getHashParams("label") || "";
    this.labelToday = el.getAttribute("data-label-today") || "";
    this.labelDaysAgo = el.getAttribute("data-label-days-ago") || "";
    this.labelExclusiveContent =
      el.getAttribute("data-label-exclusive-content") || "";

    // Pre-bind event handlers for proper cleanup
    this.boundOnViewportChange = (e: Event) =>
      this.onViewportChange(e as CustomEvent);
    this.boundOnTabItemShown = (e: Event) =>
      this.onTabItemShown(e as CustomEvent);
    this.boundOnHashChange = () => this.onHashChange();
  }

  // --- Public API ---------------------------------------------------------------

  async init(): Promise<void> {
    // AEM ships with style="display:none"; show now that JS has loaded.
    this.el.style.display = "";

    // Ensure .grid-sizer exists for Masonry.
    this.prepareGrid();

    // Build category filter UI from data-tags JSON.
    this.renderCategories();

    // Cache the article card <template> element (provided by backend).
    this.templateEl = this.el.querySelector(
      ARTICLE_TEMPLATE_SELECTOR,
    ) as HTMLTemplateElement | null;

    // Cache status elements (spinner, no-results, error).
    this.spinnerEl = this.el.querySelector(SPINNER_SELECTOR) as HTMLElement;
    this.noResultsEl = this.el.querySelector(".js-no-results") as HTMLElement;
    this.errorMessageEl = this.el.querySelector(
      ".js-error-message",
    ) as HTMLElement;

    // Status elements start hidden; JS toggles them as needed.
    if (this.spinnerEl) this.spinnerEl.style.display = "none";
    if (this.noResultsEl) this.noResultsEl.style.display = "none";
    if (this.errorMessageEl) this.errorMessageEl.style.display = "none";

    const tabComponents = Array.from(document.querySelectorAll(".comp-tab"));
    const tabHashParams = getHashParams("tab");
    const isTrueCurrentTag =
      this.currentTabId !== tabComponents[0]?.id ||
      tabHashParams === tabComponents[0]?.id ||
      !tabHashParams ||
      !tabComponents.some((tab) => tab?.id === tabHashParams);

    this.activeTab = tabComponents.length > 0 ? tabComponents[0].id : "";

    this.addEventListeners();

    // Deep-link restore (activating the tab + applying the tag from the hash) is
    // handled centrally after `load` by the coordinator in Tabs.ts, which replays
    // the entry hash through the `onHashChange` handler below. Doing it here would
    // race the tab-container init and could drop the tag / fail to load content.

    if (isTrueCurrentTag) {
      await this.initArticleList();
    }

    this.scrollToTabs();

    this.tabInitialized = isTrueCurrentTag ? this.isVisible() : false;
  }

  destroy(): void {
    document.removeEventListener("viewportChange", this.boundOnViewportChange);
    document.removeEventListener("tabItemShown", this.boundOnTabItemShown);
    window.removeEventListener("hashchange", this.boundOnHashChange);
  }

  // --- Event listeners ----------------------------------------------------------

  private addEventListeners(): void {
    // Native CustomEvent listeners (replacing Terrific events)
    document.addEventListener("viewportChange", this.boundOnViewportChange);
    document.addEventListener("tabItemShown", this.boundOnTabItemShown);

    // Hash routing
    window.addEventListener("hashchange", this.boundOnHashChange);

    // Tag filter clicks
    this.el.addEventListener("click", (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.matches(TAG_FILTER_RADIO_SELECTOR)) {
        this.handleCheckedRadioFilters(e);
      }
    });

    // Keyboard navigation for category labels
    this.el.addEventListener("keydown", (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest(".comp-article-list-category label")) {
        if (e.code === "Enter" || e.code === "Space") {
          e.preventDefault();
          const category = target.closest(".comp-article-list-category");
          const radio = category?.querySelector(
            TAG_FILTER_RADIO_SELECTOR,
          ) as HTMLElement;
          radio?.click();
        }
      }
    });

    // UDL tracking
    this.el.addEventListener("click", (e: Event) =>
      this.handleUDLs(e as MouseEvent),
    );
    this.el.addEventListener("auxclick", (e: Event) =>
      this.handleUDLs(e as MouseEvent),
    );

    // Form submit prevention
    if (this.formEl) {
      this.formEl.addEventListener("submit", (e) => e.preventDefault());
    }

    // Load more button
    const loadMoreBtn = this.el.querySelector(SHOW_MORE_BUTTON_SELECTOR);
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener("click", () => this.loadMoreResults());
    }
  }

  private onViewportChange(_e: CustomEvent): void {
    this.initLayout();
  }

  private onTabItemShown(e: CustomEvent): void {
    const data = e.detail;
    this.onShowTab(data);
  }

  private onHashChange(): void {
    const currentTabId = this.getIdOfCurrentTab();
    if (
      currentTabId === getHashParams("tab") &&
      (this.activeTab !== currentTabId ||
        this.latestHash !== window.location.hash.substr(1))
    ) {
      this.getHistoryData();
      this.initArticleList();
      this.setHistoryData();
    }
  }

  // --- Tab handling -------------------------------------------------------------

  private onShowTab(data: { id: string }): void {
    this.activeTab = data.id;
    if (this.getIdOfCurrentTab() === data.id) {
      if (!this.tabInitialized) {
        this.tabInitialized = true;
        this.tabCursorMark = data.id;
        const grid = this.el.querySelector(".grid");
        if (grid) grid.classList.remove(MASONRY_LOADED_CLASS);
        this.initArticleList();
      } else {
        this.initLayout();
      }
      this.setHistoryData();
    }
  }

  // --- Search -------------------------------------------------------------------

  private async initArticleList(): Promise<void> {
    if (this.isVisible()) {
      await this.requestSearchResults(false);
    }
  }

  private async requestSearchResults(appendResults: boolean): Promise<void> {
    if (this.cursorMark === "") {
      appendResults = false;
    }

    const pagination =
      this.initialCount !== "" ? "count." + this.initialCount : this.cursorMark;
    this.initialCount = "";
    this.tagCursorMark = this.tagCursorMark
      ? this.tagCursorMark
      : this.activeTab
        .substring(this.activeTab.indexOf("_") + 1)
        .replace("__", ".") + "__";

    try {
      const data = await this.service.query(
        [pagination, this.tagCursorMark].filter(Boolean),
      );
      this.handleSearchResults(data, appendResults);
      this.showElement(".js-error-message", false);
    } catch {
      this.showElement(".js-error-message", true);
      this.showElement(".js-spinner", false);
    }
  }

  private handleSearchResults(
    data: SearchResponse,
    appendResults: boolean,
  ): void {
    if (this.teaser) {
      this.teaser.isTeaser = true;
      data.response.docs.splice(2, 0, this.teaser);
    }

    const responseData = data.response.docs;
    responseData.forEach((item) => this.calculatePublicationDate(item));
    responseData.forEach(
      (item) => (item.label_exclusive_content = this.labelExclusiveContent),
    );

    const nextPage = data.nextCursorMark;

    if (appendResults) {
      this.searchResults = this.searchResults.concat(
        responseData.filter((obj) => obj.isTeaser !== true),
      );
    } else {
      this.searchResults = responseData;
    }

    this.resultsLength = data.response.numFound;

    this.renderArticles();
    this.handlePagination(nextPage);

    if (this.searchResults.length === 0) {
      this.showElement(".js-no-results", true);
    } else {
      this.showElement(".js-no-results", false);
    }

    this.showElement(".js-spinner", false);

    if (appendResults) {
      this.count = String(this.searchResults.length);
      this.setHistoryData();
    }
  }

  // --- Grid preparation --------------------------------------------------------

  /** Ensure the `.grid` contains a dedicated `.grid-sizer` for Masonry. */
  private prepareGrid(): void {
    const grid = this.el.querySelector(".grid");
    if (!grid) return;

    // Ensure a dedicated .grid-sizer exists for Masonry's columnWidth option.
    // Must be an <li> (not <div>) because column-control CSS targets
    // `.row > div:not([class*="col-"])` and forces width:100%.
    if (!grid.querySelector(".grid-sizer")) {
      const sizer = document.createElement("li");
      sizer.className = "grid-sizer";
      grid.prepend(sizer);
    }
  }

  // --- Category filters ---------------------------------------------------------

  /**
   * Build category filter radio buttons from `this.tags` (parsed from
   * the `data-tags` data attribute).
   */
  private renderCategories(): void {
    if (!this.tags.length) return;

    const categoriesEl = this.el.querySelector(".categories");
    if (!categoriesEl) return;

    this.tags.forEach((tag) => {
      const li = document.createElement("li");
      li.className = "comp-article-list-category";

      const input = document.createElement("input");
      input.type = "radio";
      input.name = "article_list_tag";
      input.className = "js-tag-filter-input-radio";
      input.id = tag.id;
      input.value = tag.text;

      const label = document.createElement("label");
      label.tabIndex = 0;
      label.setAttribute("for", tag.id);
      label.setAttribute("data-text", tag.text);
      if (tag.capitalized) {
        label.classList.add("article-list-category-capitalized");
      }

      const span = document.createElement("span");
      span.className = "js-category-input-radio-label";
      span.textContent = tag.text;

      label.appendChild(span);
      li.appendChild(input);
      li.appendChild(label);
      categoriesEl.appendChild(li);
    });
  }

  // --- Rendering ----------------------------------------------------------------

  /**
   * Render article cards by cloning `<template class="js-article-template">`
   * and populating its data-* placeholder attributes.
   */
  private renderArticles(): void {
    const grid = this.el.querySelector(".grid");
    if (!grid || !this.templateEl) return;

    // Remove previous article items (keep .grid-sizer)
    grid
      .querySelectorAll(".grid-item:not(.grid-sizer)")
      .forEach((item) => item.remove());

    this.searchResults.forEach((item) => {
      const clone = this.templateEl!.content.cloneNode(
        true,
      ) as DocumentFragment;

      // --- Link ---
      const link = clone.querySelector("[data-href]") as HTMLAnchorElement;
      if (link) {
        link.setAttribute("href", item.externalizedPath || "");
        link.removeAttribute("data-href");
      }

      // --- Teaser modifier ---
      // TODO: comp-article--teaser has no CSS anywhere in the codebase — investigate if styles are needed
      if (item.isTeaser) {
        const article = clone.querySelector(".comp-article") as HTMLElement;
        if (article) article.classList.add("comp-article--teaser");
      }

      // --- Image block ---
      const imageBlock = clone.querySelector(".comp-image") as HTMLElement;
      if (item.imageFileReference && imageBlock) {
        // Responsive <source> srcsets
        const setSource = (attr: string, value: string | undefined) => {
          const el = clone.querySelector(`[${attr}]`);
          if (el && value) {
            el.setAttribute("srcset", value);
          }
          el?.removeAttribute(attr);
        };
        setSource("data-srcset-xs", item.imageFileReferenceXs);
        setSource("data-srcset-s", item.imageFileReferenceS);
        setSource("data-srcset-m", item.imageFileReferenceM);
        setSource("data-srcset-l", item.imageFileReferenceL);
        setSource("data-srcset-xl", item.imageFileReferenceXl);

        // Fallback <img>
        const img = clone.querySelector("[data-src]") as HTMLImageElement;
        if (img) {
          img.setAttribute("src", item.imageFileReference);
          img.removeAttribute("data-src");
        }

        // Premium badge
        const premium = clone.querySelector("[data-premium]") as HTMLElement;
        if (premium) {
          if (item.premium && item.label_exclusive_content) {
            premium.classList.remove("hidden");
            premium.ariaHidden = "false";
            premium.classList.add("premium--visible");
            premium.setAttribute(
              "data-universal-tooltip",
              item.label_exclusive_content,
            );
            premium.setAttribute("aria-label", item.label_exclusive_content);
          }
          premium.removeAttribute("data-premium");
        }

        // Channel icon
        const channelIcon = clone.querySelector(
          "[data-channel-icon]",
        ) as HTMLImageElement;
        if (channelIcon) {
          if (item.channelIconFileReference) {
            channelIcon.classList.remove("hidden");
            channelIcon.ariaHidden = "false";
            channelIcon.setAttribute("src", item.channelIconFileReference);
            channelIcon.setAttribute("alt", item.channelIconAltText || "");
          }
          channelIcon.removeAttribute("data-channel-icon");
        }
      } else if (imageBlock) {
        imageBlock.remove();
      }

      // --- Card bottom (text) ---
      const keyTopic = clone.querySelector("[data-key-topic]") as HTMLElement;
      if (keyTopic) {
        if (item.key_topic) {
          keyTopic.textContent = item.key_topic;
        } else {
          keyTopic.remove();
        }
      }

      const pubDate = clone.querySelector("[data-pub-date]") as HTMLElement;
      if (pubDate) {
        if (item.publication_date_localized) {
          pubDate.textContent = item.publication_date_localized;
        } else {
          pubDate.remove();
        }
      }

      const title = clone.querySelector("[data-title]") as HTMLElement;
      if (title) {
        title.textContent = item.title || "";
      }

      const description = clone.querySelector(
        "[data-description]",
      ) as HTMLElement;
      if (description) {
        if (item.seoDescription) {
          description.textContent = item.seoDescription;
        } else {
          description.remove();
        }
      }

      // --- Author list ---
      const authorContainer = clone.querySelector(
        "[data-author-list]",
      ) as HTMLElement;
      if (authorContainer) {
        if (item.authorList && item.authorList.length > 0) {
          authorContainer.appendChild(this.renderAuthorList(item.authorList));
        }
        authorContainer.removeAttribute("data-author-list");
      }

      grid.appendChild(clone);
    });

    this.initLayout();
  }

  /**
   * Render the author list matching the three KO template variants:
   * - 1 author: image + name
   * - 2–5 authors: images only
   * - 6+ authors: first 4 thumbnails + "+N" indicator
   */
  private renderAuthorList(authors: any[]): HTMLUListElement {
    const ul = document.createElement("ul");
    ul.className = "article__authors";

    const showName = authors.length === 1;
    const showMore = authors.length >= 6;
    const visibleAuthors = showMore ? authors.slice(0, 4) : authors;

    for (const author of visibleAuthors) {
      const li = document.createElement("li");
      li.className = "article__author";

      const imgDiv = document.createElement("div");
      imgDiv.className = "article__authors-image";

      if (author.imageSrc) {
        const img = document.createElement("img");
        img.alt = "";
        img.src = author.imageSrc;
        imgDiv.appendChild(img);
      }

      li.appendChild(imgDiv);

      if (showName && author.fullName) {
        const nameSpan = document.createElement("span");
        nameSpan.className = "article__author-name";
        nameSpan.textContent = author.fullName;
        li.appendChild(nameSpan);
      }

      ul.appendChild(li);
    }

    if (showMore) {
      const li = document.createElement("li");
      li.className = "article__author";
      const moreDiv = document.createElement("div");
      moreDiv.className = "article__authors-image article__authors-image--more";
      const span = document.createElement("span");
      span.textContent = `+${authors.length - 4}`;
      moreDiv.appendChild(span);
      li.appendChild(moreDiv);
      ul.appendChild(li);
    }

    return ul;
  }

  // --- Pagination ---------------------------------------------------------------

  private handlePagination(nextCursorMark?: string): void {
    const btn = this.el.querySelector(SHOW_MORE_BUTTON_SELECTOR) as HTMLElement;
    if (btn) {
      btn.style.display = nextCursorMark ? "" : "none";
    }
    this.cursorMark = nextCursorMark || "";
  }

  private loadMoreResults(): void {
    this.requestSearchResults(true);
  }

  // --- Tag filtering ------------------------------------------------------------

  private handleCheckedRadioFilters(event: Event): void {
    this.count = "";
    this.initialCount = "";
    this.cursorMark = "";

    const grid = this.el.querySelector(".grid");
    if (grid) grid.classList.remove(MASONRY_LOADED_CLASS);

    setTimeout(() => {
      this.toggleTag(event);
      this.requestSearchResults(false);
      this.setHistoryData();
    }, 200);
  }

  private toggleTag(event: Event): void {
    const target = event.target as HTMLElement;
    const foundTags = this.tags.filter((tag) => tag.id === target.id);
    if (foundTags.length === 0) return;

    const currentTagId = foundTags[0].id;
    const currentTagElement = document.getElementById(currentTagId);
    if (!currentTagElement) return;

    const tabContainer = currentTagElement.closest(".comp-tab-container");
    if (tabContainer) {
      tabContainer.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    if (currentTagElement.getAttribute("checked") === "checked") {
      this.setIsCheckedTag(currentTagElement, false);
      this.tagCursorMark =
        this.el.getAttribute("data-url-initial-params") || "";
    } else {
      // Uncheck others
      const categories = currentTagElement.closest(".categories");
      if (categories) {
        categories.querySelectorAll("input").forEach((input) => {
          if (input.id !== currentTagId) {
            this.setIsCheckedTag(input, false);
          }
        });
      }
      this.setIsCheckedTag(currentTagElement, true);
      this.tagCursorMark = foundTags[0].cursorMark || "";
    }
  }

  private setIsCheckedTag(
    element: HTMLElement | null,
    isChecked: boolean,
  ): void {
    if (!element) return;
    (element as HTMLInputElement).checked = Boolean(isChecked);
    if (isChecked) {
      element.setAttribute("checked", "checked");
    } else {
      element.removeAttribute("checked");
    }
  }

  // --- Layout -------------------------------------------------------------------

  private async initLayout(): Promise<void> {
    const grid = this.el.querySelector(".grid") as HTMLElement;
    if (!grid) return;

    this.msnry = new (window as any).Masonry(grid, {
      itemSelector: ".grid-item",
      columnWidth: ".grid-sizer",
      percentPosition: true,
    });

    const layoutMasonry = () => {
      this.msnry.layout();
      grid.classList.add(MASONRY_LOADED_CLASS);
    };

    const images = this.el.querySelectorAll(".grid .comp-image img");

    if (images.length) {
      await waitForImages(grid);
      layoutMasonry();
    } else {
      layoutMasonry();
    }
  }

  // --- Scroll -------------------------------------------------------------------

  private scrollToTabs(): void {
    const checkedRadio = this.el.querySelector(
      ".categories .js-tag-filter-input-radio[checked]",
    );
    if (checkedRadio) {
      const tabContainer = this.el.closest(".comp-tab-container");
      if (tabContainer) {
        (tabContainer as HTMLElement).scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        focusUnfocusableElement(tabContainer as HTMLElement);
      }
    }
  }

  // --- History / Hash routing ---------------------------------------------------

  private setHistoryData(): void {
    if (!this.tabCursorMark || this.tabCursorMark === "") {
      this.tabCursorMark = this.getIdOfCurrentTab();
    }

    const params: Record<string, string> = {
      tab: this.tabCursorMark,
      tag: this.tagCursorMark,
    };
    if (this.count) {
      params.count = this.count;
    }
    if (
      this.label &&
      this.tagCursorMark &&
      this.tagCursorMark.indexOf("tag.") === 0
    ) {
      params.label = this.label;
    }

    this.latestHash = new URLSearchParams(
      params as Record<string, string>,
    ).toString();
    window.location.hash = this.latestHash;
  }

  private getHistoryData(): void {
    const tab = getHashParams("tab") || Object.keys(getHashParams() || {})[0];

    if (this.getIdOfCurrentTab() === tab) {
      const tag = getHashParams("tag");
      const rawLabel = getHashParams("label");
      const label = rawLabel ? decodeURIComponent(rawLabel) : "";

      this.tagCursorMark = tag || "";
      this.initialCount = getHashParams("count") || "";
      this.count = this.initialCount;
      this.cursorMark = "";

      const tabLink = document.querySelector<HTMLElement>(
        "a[href$='" + tab + "']",
      );

      if (tabLink) {
        // The tab link is an <a href="#<tab-id>">. Its default navigation
        // would overwrite the full "#tab=…&tag=…" hash with the short
        // "#<tab-id>" form, dropping the tag and leaving no articles. The
        // tab-container normally preventDefault()s this, but it may not be
        // initialised yet (init-order race), so guard the click here
        // regardless of the tab-container's state.
        const preventNavigation = (e: Event) => e.preventDefault();
        tabLink.addEventListener("click", preventNavigation);
        tabLink.click();
        tabLink.removeEventListener("click", preventNavigation);
      }

      if (this.isVisible()) {
        this.tags.forEach((t) => {
          const element = document.getElementById(t.id);
          this.setIsCheckedTag(element, t.cursorMark === this.tagCursorMark);
        });
      }
    }
  }

  // --- UDL tracking -------------------------------------------------------------

  private handleUDLs(event: MouseEvent): void {
    if (!isMainOrMiddleClick(event)) return;
    if (!document.body.classList.contains(MAGAZINE_ARTICLE_PAGE_CLASS)) return;

    const target = event.target as HTMLElement;
    const link = target.closest(
      ARTICLE_LINK_SELECTOR,
    ) as HTMLAnchorElement | null;

    if (link) {
      const text = link.querySelector(CARD_TITLE_SELECTOR)?.textContent || "";
      const url = link.href || "";
      handleCustomUDL("youtooth_detail_related_post_click", {
        link_text: text,
        link_url: url,
      });
    } else if (
      target.closest(SHOW_MORE_BUTTON_SELECTOR) &&
      event.button === MOUSE_EVENT_BUTTON.MAIN
    ) {
      handleCustomUDL("youtooth_detail_related_post_load_more");
    }
  }

  // --- Dates --------------------------------------------------------------------

  private calculatePublicationDate(item: any): void {
    const dateString = dateStringCreator.createDateFromLabels(
      item.publication_date_iso,
      this.labelToday,
      this.labelDaysAgo,
    );
    if (dateString && dateString.length > 0) {
      item.publication_date_localized = dateString;
    }
  }

  // --- Utilities ----------------------------------------------------------------

  private getIdOfCurrentTab(): string {
    const tabParent = this.el.closest(TAB_CLASS_NAME);
    return tabParent?.id || "";
  }

  private isVisible(): boolean {
    return !!(
      this.el.offsetWidth ||
      this.el.offsetHeight ||
      this.el.getClientRects().length
    );
  }

  private showElement(selector: string, show: boolean): void {
    const refs: Record<string, HTMLElement | null> = {
      ".js-spinner": this.spinnerEl,
      ".js-no-results": this.noResultsEl,
      ".js-error-message": this.errorMessageEl,
    };
    const el =
      refs[selector] ?? (this.el.querySelector(selector) as HTMLElement);
    if (el) {
      el.style.display = show ? "" : "none";
    }
  }

  // --- Static bootstrap ---------------------------------------------------------

  /**
   * Find all `.comp-article-list` elements in the page and initialise an
   * ArticleList instance for each one.  Returns the created instances so
   * callers can hold references (e.g. for later `destroy()`).
   */
  static async initAll(): Promise<ArticleList[]> {
    const elements =
      document.querySelectorAll<HTMLElement>(".comp-article-list");
    const instances: ArticleList[] = [];

    for (const el of Array.from(elements)) {
      const instance = new ArticleList(el);
      await instance.init();
      instances.push(instance);
    }

    return instances;
  }
}
